import _                                              from 'underscore';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Container, Header, List, Segment }           from 'semantic-ui-react';
import { useQuery }                                   from '@apollo/react-hooks';
import { useRouteMatch }                              from 'react-router-dom';

import { CorvalAccount, CorvalOrganizationAccount, FlexAlign, FlexJustify, RouterLocationParams, SemanticUISize, Size } from '@src/types';

import { queriesHaveData, queriesLoading }                                        from '@src/store/_apolloUtil';
import { SystemAccountingQueries }                                                from '@src/queries';
import { UserService }                                                            from '@src/services';
import { useCurrentUser }                                                         from '@src/hooks';
import { Flexbox, FlexItem, Page, PageMeta, PageTitleContainer, StyledContainer } from '@src/views/containers';
import { CorvalStylizedHexContainer }                                             from '@src/views/components/product/CorvalStylizedHexContainer';
import { LoadingIndicator }                                                       from '@src/views/components/util/loading/LoadingIndicator';
import { FinePrintMessage }                                                       from '@src/views/components/util/messaging/FinePrintMessage';
import { IntercomIdentifier }                                                     from '@src/views/components/util/external/IntercomIdentifier';
import { UserContext, UserContextType }                                           from '../UserIndexPage';
import { UserProjectListing }                                                     from './UserProjectListing';
import { UserProfileCard }                                                        from './UserProfileCard';


interface UserProfilePageProps {
    [ additionalProps: string ]: any;
}

const defaultProps: UserProfilePageProps = {};

const UserProfilePage: FC<UserProfilePageProps> = ( props ) => {

    let { params }                      = useRouteMatch<RouterLocationParams.UserProfile>(),
        { currentUserReq, currentUser } = useCurrentUser(),
        { accountId, organizationId }   = useContext<UserContextType>( UserContext ),
        [ userAccount, setUserAccount ] = useState<CorvalAccount & { organizationRole: CorvalOrganizationAccount['organizationRole'] }>( null ),
        [ isCurrentUsersProfile ]       = useState( params.accountId === currentUser.id );

    let getOrganizationAccountReq: any = useQuery( SystemAccountingQueries.queries.GET_ORGANIZATIONS(
        `organizationMembers{
			organizationRole
			account{
				id
				createdUtc
				firstName
				lastName
				emailAddress
				businessFunctions
			}
		}` ), {
        variables:   { id: organizationId },
        fetchPolicy: 'network-only'
    } );

    let isLoading       = ( props.loading || queriesLoading( [ getOrganizationAccountReq, currentUserReq ] ) ),
        loadingLanguage = ( queriesLoading( getOrganizationAccountReq ) ) ? 'Gathering data...' : null;

    useEffect( () => {

        if( queriesHaveData( getOrganizationAccountReq ) ){

            let users = _.chain( getOrganizationAccountReq.data.organizations ).pluck( 'organizationMembers' ).flatten().uniq( u => u.account.id ).value(),
                user  = _.find( users, u => u.account.id === accountId ) ?? null;

            if( user ){
                setUserAccount( { ...user.account, organizationRole: user.organizationRole } );
            }
        }

    }, [ getOrganizationAccountReq ] );

    return (

        <Page name={ 'user-profile' }
              padMainHorizontal={ false }
              padMainVertical={ false }
              allowContentScroll={ true }>

            <CorvalStylizedHexContainer fill={ 'area' } size={ SemanticUISize.MINI } fadeDirection={ 'down' }/>

            <PageTitleContainer title={ `${ isCurrentUsersProfile ? 'My' : userAccount ? `${ UserService.getUserName( userAccount ) }'s` : 'User' } Profile` }/>

            <PageMeta section={ 'Users' } title={ ( isLoading && userAccount ) ? 'User Profile' : `${ UserService.getUserName( userAccount ) }'s Profile` }>

                <LoadingIndicator className={ null } label={ loadingLanguage } isActive={ isLoading } inverted/>

                { !isLoading && (

                    <Container className={ 'padding-lg-vertical fadeIn' }>

                        <Segment raised>
                            <StyledContainer padding={ Size.MD }>

                                { userAccount && (
                                    <Flexbox align={ FlexAlign.START } justify={ FlexJustify.SPACE_BETWEEN }>

                                        <FlexItem noGrow>

                                            <UserProfileCard account={ userAccount } organizationRole={ userAccount.organizationRole }/>

                                        </FlexItem>

                                        <FlexItem basis={ 20 } className={ 'margin-lg-horizontal' }>

                                            <Header as={ 'h4' } content={ 'Projects' } dividing/>
                                            <FinePrintMessage>Below is a list of projects this person is a team member on:</FinePrintMessage>
                                            <UserProjectListing accountId={ accountId } organizationId={ organizationId }/>

                                        </FlexItem>
                                        <FlexItem basis={ 20 } noGrow>

                                            <Header as={ 'h4' } content={ `Roles` } dividing/>
                                            { ( userAccount.businessFunctions.length > 0 )
                                              ? <List>
                                                  { userAccount.businessFunctions.map( functionKey => (
                                                      <List.Item key={ functionKey }>
                                                          <List.Icon name={ 'check' } color={ 'purple' }/>
                                                          <List.Content>{ functionKey }</List.Content>
                                                      </List.Item>
                                                  ) ) }
                                              </List>
                                              : <FinePrintMessage>This user has not specified any team functions.</FinePrintMessage> }

                                        </FlexItem>

                                    </Flexbox>
                                ) }


                            </StyledContainer>
                        </Segment>
                    </Container>

                ) }

            </PageMeta>

            <IntercomIdentifier/>

        </Page>
    );
};

UserProfilePage.defaultProps = defaultProps;

export { UserProfilePage };
